// ProfileModal.tsx — Sprint 14. The "My Profile" dropdown modal.
// Form instance passed in by the orchestrator, which prefills it at open time
// from the live store user and saves via the SAME self-scoped store action the
// My Home "Edit profile" path uses (PUT /api/auth/user/{id}).
//
// Sprint 144 (R14-5, ADR-139) — this modal used to be a dead duplicate of that
// working surface: Save ran a 1s timer and toasted success with ZERO network
// calls. It is now wired to the real endpoint. Two controls that the endpoint
// does NOT back were REMOVED rather than left decorative — "Change Avatar" had
// no handler at all, and "Bio" had nowhere to persist to (there is no bio field
// on the user record). The rule from R11-1/R12-1/R13-4 holds: a control must
// either work or not exist. `Position` stays as a disabled, role-derived label
// — it is displayed truth, not an editable field pretending to save.
import React from 'react';
import {
  Avatar, Button, Col, Form, FormInstance, Input, Modal, Row, Space, Typography,
} from 'antd';
import {
  CheckOutlined, MailOutlined, PhoneOutlined, ProfileOutlined,
  UserOutlined,
} from '@ant-design/icons';

const { Title, Text } = Typography;

export interface ProfileModalProps {
  open:    boolean;
  form:    FormInstance;
  user:    any;
  /** True while the real PUT is in flight (Sprint 144). */
  saving?: boolean;
  onCancel: () => void;
  onSave:   () => void;
}

export const ProfileModal: React.FC<ProfileModalProps> = ({
  open, form, user, saving = false, onCancel, onSave,
}) => {
  // `user` is `any` from the orchestrator's `useAuth()` return, so the
  // comparison is loose by design — covers both the modern role names
  // and the legacy 'superadmin' / 'super_admin' values still present
  // in older records.
  const role = user?.role as string | undefined;
  const isAdminTier = role === 'super_admin'
    || role === 'superadmin'
    || role === 'super_admin_condo';
  const positionLabel = isAdminTier ? 'System Administrator'
    : role === 'admin' ? 'Property Manager'
    : 'Resident';

  return (
    <Modal
      title={<Space><ProfileOutlined style={{ color: '#4299e1' }} /><span>My Profile</span></Space>}
      open={open}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={saving}>Cancel</Button>,
        <Button key="save" type="primary" loading={saving} onClick={onSave} icon={<CheckOutlined />}>
          Save Changes
        </Button>,
      ]}
      width={600}
      centered
    >
      <Form form={form} layout="vertical">
        <Row gutter={24}>
          <Col span={24}>
            <div style={{
              display: 'flex', alignItems: 'center', marginBottom: 24,
              padding: 16, background: '#f8fafc', borderRadius: 8,
            }}>
              <Avatar
                size={64} icon={<UserOutlined />}
                style={{
                  background: 'linear-gradient(135deg, #4299e1 0%, #3182ce 100%)',
                  marginRight: 16,
                }}
              />
              <div>
                <Title level={5} style={{ margin: 0 }}>{user?.name || 'User Profile'}</Title>
                <Text type="secondary">{positionLabel}</Text>
              </div>
            </div>
          </Col>

          <Col span={12}>
            <Form.Item
              name="name" label="Full Name"
              rules={[{ required: true, message: 'Please enter your name' }]}
            >
              <Input prefix={<UserOutlined />} placeholder="Enter your full name" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="email" label="Email Address"
              rules={[
                { required: true, message: 'Please enter your email' },
                { type: 'email',  message: 'Please enter a valid email' },
              ]}
            >
              <Input prefix={<MailOutlined />} placeholder="Enter your email" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="phone" label="Phone Number" rules={[{ max: 50, message: 'Phone number is too long' }]}>
              <Input prefix={<PhoneOutlined />} placeholder="Enter your phone number" maxLength={50} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};
